import React, { useCallback, useEffect, useState } from 'react';

import styled, { ThemeProvider } from 'styled-components';

import Banner from 'atoms/Banner';
import Box from 'atoms/Box';
import Button from 'atoms/Button';
import Checkbox from 'atoms/Checkbox';
import Column from 'atoms/Column';
import ContentWrapper from 'atoms/ContentWrapper';
import Divider from 'atoms/Divider';
import Row from 'atoms/Row';
import Setting from 'atoms/Setting';

import Description from 'molecules/Description';
import PrivacyPolicy from 'molecules/PrivacyPolicy';
import SettingContent from 'molecules/SettingContent';

import Header from 'organisms/Header';

import Localized, { Strings } from 'utils/localize';

import GlobalStyles from 'GlobalStyles';
import { analyticCookieName, setCookie, settingsCookies } from 'banner.manager';

import { theme } from 'theme';

// eslint-disable-next-line max-lines-per-function
function App() {
  const initialState = settingsCookies()[analyticCookieName];

  const [isAnalyticsCookiesActive, setIsAnalyticsCookiesActive] = useState(
    initialState !== null ? initialState : true
  );

  const isSettingsEquals = isAnalyticsCookiesActive === initialState;

  const [
    isConfirmationButtonDisabled,
    setIsConfirmationButtonDisabled,
  ] = useState(isSettingsEquals);

  const toggleAnalyticsCookie = useCallback(() => {
    setIsAnalyticsCookiesActive(!isAnalyticsCookiesActive);
  }, [isAnalyticsCookiesActive, setIsAnalyticsCookiesActive]);

  const onConfirm = useCallback(() => {
    setCookie(analyticCookieName, isAnalyticsCookiesActive);
    setCookie();
    setIsConfirmationButtonDisabled(true);
  }, [isAnalyticsCookiesActive]);

  useEffect(() => {
    setIsConfirmationButtonDisabled(isSettingsEquals);
  }, [isSettingsEquals, setIsConfirmationButtonDisabled]);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <ContentWrapper>
        <Header />
        <Banner>{Localized(Strings.title)}</Banner>
        <InnerContentWrapper>
          <Description />
          <Divider />
          <SettingsRow>
            <Column>
              <Setting>
                <SettingContentExtended
                  level="h3"
                  heading={Localized(Strings.options.required.title)}
                  headingMt={0}
                  text={Localized(Strings.options.required.text)}
                  textSize={1.15}
                />
                <Checkbox isActive disabled />
              </Setting>
            </Column>
            <Divider />
            <Column>
              <Setting>
                <SettingContentExtended
                  level="h3"
                  heading={Localized(Strings.options.advanced.title)}
                  headingMt={0}
                  text={Localized(Strings.options.advanced.text)}
                  textSize={1.15}
                />
                <Checkbox
                  isActive={isAnalyticsCookiesActive}
                  onClick={toggleAnalyticsCookie}
                />
              </Setting>
            </Column>
            <Divider />
          </SettingsRow>
          <PrivacyPolicy />
          <Box mb={4.25}>
            <Button disabled={isConfirmationButtonDisabled} onClick={onConfirm}>
              {isConfirmationButtonDisabled
                ? Localized(Strings.button.saved)
                : Localized(Strings.button.save)}
            </Button>
          </Box>
          <SettingContent
            heading={Localized(Strings.description.title)}
            text={Localized(Strings.description.text)}
          />
        </InnerContentWrapper>
      </ContentWrapper>
    </ThemeProvider>
  );
}

const SettingsRow = styled(Row)`
  flex-direction: column;
  width: 100%;
`;

const InnerContentWrapper = styled(Box)`
  max-width: 704px;
  margin-right: auto;
  margin-left: auto;
  padding: 0 1em;

  font-size: 16px;

  ${theme.devices.tablet`
    font-size: 28px;
    padding-bottom: 4em;
  `}
`;

const SettingContentExtended = styled(SettingContent)`
  padding-right: 1.15em;
`;

export default App;
